/**
 * iOS WebKit throws NotFoundError (DOMException code 8) when React's commit
 * phase calls removeChild or insertBefore on a node the browser already
 * reparented — page translation, autofill, or Radix scroll-lock. parentNode
 * can still point at the caller when the native call throws. Swallow only
 * that mismatch so the root error boundary does not blank the app.
 * Every other call, including download-anchor removal, goes to the native method.
 */

const NOT_FOUND_ERR = 8;

function isNotFoundDomError(error: unknown): boolean {
  return error instanceof DOMException && error.code === NOT_FOUND_ERR;
}

let installed = false;

export function installExternalDomGuard(): void {
  if (installed || typeof Node !== 'function') return;
  installed = true;

  const proto = Node.prototype;
  const originalRemoveChild = proto.removeChild;
  const originalInsertBefore = proto.insertBefore;

  proto.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) return child;
    try {
      return originalRemoveChild.call(this, child) as T;
    } catch (error) {
      if (isNotFoundDomError(error)) return child;
      throw error;
    }
  };

  proto.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    node: T,
    child: Node | null
  ): T {
    if (child !== null && child.parentNode !== this) {
      return originalInsertBefore.call(this, node, null) as T;
    }
    try {
      return originalInsertBefore.call(this, node, child) as T;
    } catch (error) {
      if (isNotFoundDomError(error)) return node;
      throw error;
    }
  };
}
